class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const priceInputNames = new Set(
      Array.from(document.querySelectorAll('price-range input.field__input')).map((el) => el.name)
    );

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      const sanitized = priceInputNames.has(key) ? parseMoneyToNumberForSubmission(value) : value;
      params.append(key, sanitized);
    });
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

function parseMoneyToNumberForSubmission(val) {
  if (val === undefined || val === null) return '';
  const raw = String(val).trim();
  if (raw === '') return '';
  let s = raw.replace(/[^0-9,\.\-]/g, '');
  s = s.replace(/\./g, '').replace(/,/g, '.');
  const num = Number(s);
  return isNaN(num) ? '' : String(num);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.textInputs = Array.from(this.querySelectorAll('input.field__input'));
    this.sliderMin = this.querySelector('.price-range-slider__input--min');
    this.sliderMax = this.querySelector('.price-range-slider__input--max');
    this.rangeBar = this.querySelector('.price-range-slider__range');
    this.sliderMaxValue = this.sliderMax ? Number(this.sliderMax.getAttribute('max')) : 0;
    this.minGap = this.getMinGap();
    this.currencySymbol = (this.textInputs[0]?.dataset?.symbol || this.querySelector('.field-currency')?.textContent || '').trim();

    this.textInputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    if (this.sliderMin && this.sliderMax) {
      const onSliderInput = this.onSliderInput.bind(this);
      const onSliderChange = this.onSliderChange.bind(this);
      this.sliderMin.addEventListener('input', onSliderInput);
      this.sliderMax.addEventListener('input', onSliderInput);
      this.sliderMin.addEventListener('change', onSliderChange);
      this.sliderMax.addEventListener('change', onSliderChange);
    }

    this.setMinAndMaxValues();
    this.syncSliderFromText();
    this.updateSliderBar();
    this.applyCurrencyPrefixToTextInputs();
  }

  onRangeChange(event) {
    this.setMinAndMaxValues();
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSliderFromText();
    this.updateSliderBar();
    this.applyCurrencyPrefixToTextInputs();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  onSliderInput(event) {
    const isMin = event && event.target === this.sliderMin;
    const isMax = event && event.target === this.sliderMax;
    const minVal = Number(this.sliderMin.value);
    const maxVal = Number(this.sliderMax.value);
    const gap = this.minGap;
    if (isMin && minVal > maxVal - gap) {
      const clamped = Math.min(this.sliderMaxValue - gap, Math.max(0, maxVal - gap));
      this.sliderMin.value = clamped;
    } else if (isMax && maxVal < minVal + gap) {
      const clamped = Math.max(gap, Math.min(this.sliderMaxValue, minVal + gap));
      this.sliderMax.value = clamped;
    }
    this.updateSliderBar();
  }

  onSliderChange() {
    const minVal = Number(this.sliderMin.value);
    const maxVal = Number(this.sliderMax.value);
    if (maxVal - minVal < this.minGap) {
      this.sliderMax.value = Math.min(this.sliderMaxValue, minVal + this.minGap);
    }
    this.syncTextFromSlider();
    this.applyCurrencyPrefixToTextInputs();
    this.setMinAndMaxValues();
    this.textInputs.forEach((el) => el.dispatchEvent(new Event('input', { bubbles: true })));
  }

  getTextInputs() {
    const minInput = this.textInputs && this.textInputs[0];
    const maxInput = this.textInputs && this.textInputs[1];
    return [minInput, maxInput];
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.getTextInputs();
    if (!minInput || !maxInput) return;
    const gap = this.minGap;
    const minVal = this.parseCurrencyToNumber(minInput.value || 0);
    const maxVal = this.parseCurrencyToNumber(maxInput.value || this.sliderMaxValue);

    if (maxInput.value !== '') {
      minInput.setAttribute('data-max', String(Math.max(0, Math.min(this.sliderMaxValue, maxVal - gap))));
    } else {
      minInput.setAttribute('data-max', String(this.sliderMaxValue - gap));
    }

    if (minInput.value !== '') {
      maxInput.setAttribute('data-min', String(Math.max(0, minVal + gap)));
    } else {
      maxInput.setAttribute('data-min', String(0));
    }
  }

  adjustToValidValues(input) {
    const value = this.parseCurrencyToNumber(input.value);
    const min = this.parseCurrencyToNumber(input.getAttribute('data-min'));
    const max = this.parseCurrencyToNumber(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  parseCurrencyToNumber(val) {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    let s = String(val).trim();
    s = s.replace(/[^0-9,\.\-]/g, '');
    s = s.replace(/\./g, '').replace(/,/g, '.');
    const num = Number(s);
    return isNaN(num) ? 0 : num;
  }

  syncSliderFromText() {
    if (!this.sliderMin || !this.sliderMax) return;
    const [minInput, maxInput] = this.getTextInputs();
    if (!minInput || !maxInput) return;

    const minVal = this.parseCurrencyToNumber(minInput.value || 0);
    const maxVal = this.parseCurrencyToNumber(maxInput.value || this.sliderMaxValue);

    let newMin = Math.max(0, Math.min(minVal, this.sliderMaxValue));
    let newMax = Math.max(0, Math.min(maxVal, this.sliderMaxValue));
    if (newMax - newMin < this.minGap) {
      if (newMax + this.minGap <= this.sliderMaxValue) {
        newMax = newMin + this.minGap;
      } else {
        newMin = Math.max(0, newMax - this.minGap);
      }
    }
    this.sliderMin.value = newMin;
    this.sliderMax.value = newMax;
  }

  syncTextFromSlider() {
    const [minInput, maxInput] = this.getTextInputs();
    if (!minInput || !maxInput || !this.sliderMin || !this.sliderMax) return;
    minInput.value = this.formatWithCurrency(this.sliderMin.value);
    maxInput.value = this.formatWithCurrency(this.sliderMax.value);
  }

  updateSliderBar() {
    if (!this.rangeBar || !this.sliderMin || !this.sliderMax || !this.sliderMaxValue) return;
    const minPercent = (Number(this.sliderMin.value) / this.sliderMaxValue) * 100;
    const maxPercent = (Number(this.sliderMax.value) / this.sliderMaxValue) * 100;
    this.rangeBar.style.left = `${Math.min(minPercent, maxPercent)}%`;
    this.rangeBar.style.right = `${100 - Math.max(minPercent, maxPercent)}%`;
  }

  getMinGap() {
    return 5;
  }

  formatWithCurrency(val) {
    if (val === undefined || val === null || String(val).trim() === '') return '';
    const num = Number(val);
    const formatted = isNaN(num)
      ? String(val)
      : num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return this.currencySymbol ? `${this.currencySymbol} ${formatted}` : formatted;
  }

  applyCurrencyPrefixToTextInputs() {
    if (!this.currencySymbol) return;
    this.textInputs.forEach((el) => {
      const raw = String(el.value || '').trim();
      if (raw === '') return;
      const numeric = this.parseCurrencyToNumber(raw);
      if (!isNaN(numeric)) {
        el.value = this.formatWithCurrency(numeric);
      }
    });
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
